import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { Clearfix } from 'react-bootstrap';
import { injectIntl } from 'react-intl';
import moment from 'moment';
import 'moment-timezone';

import { fetchCalendar } from 'actions/CalendarActions';
import { getTimezoneFromState } from 'helpers/intl';

import CalendarWeekEvent from 'components/layout/calendar/CalendarWeekEvent';

class CalendarWeekView extends React.Component {
  componentDidMount = () => {
    const { date } = this.props;
    this.updateCalendar(date);
  };

  componentDidUpdate = prevProps => {
    const { date } = this.props;

    const oldDate = moment(prevProps.date);
    const newDate = moment(date);

    if (oldDate.month() !== newDate.month() || oldDate.year() !== newDate.year()) {
      this.updateCalendar(date);
    }
  };

  updateCalendar = date => {
    const currentLocaleData = moment.localeData();

    const monthStartsWith = moment(date)
      .clone()
      .startOf('month')
      .day();
    const daysInMonth = moment(date).daysInMonth();

    /* see how many blank entries we have to create  */
    const blankBeginningCount =
      monthStartsWith - currentLocaleData._week.dow < 0
        ? monthStartsWith - currentLocaleData._week.dow + 7
        : monthStartsWith - currentLocaleData._week.dow;

    /* see how many blank entries at the end we have to create  */
    const blankEndingCount =
      Math.ceil((daysInMonth + blankBeginningCount) / 7) * 7 - (daysInMonth + blankBeginningCount);

    let startDate;
    let endDate;
    if (blankBeginningCount) {
      startDate = moment(date)
        .subtract(1, 'months')
        .date(
          moment(date)
            .subtract(1, 'month')
            .daysInMonth() -
            blankBeginningCount +
            1
        );
    } else {
      startDate = moment(date).date(1);
    }

    if (blankEndingCount) {
      endDate = moment(date)
        .add(1, 'months')
        .date(blankEndingCount);
    } else {
      endDate = moment(date).date(moment(date).daysInMonth());
    }

    this.props.doFetchCalendar(startDate.format('YYYY-MM-DD'), endDate.format('YYYY-MM-DD'));
  };

  // render hours list for given locale
  hoursList = () => {
    const { limitHours } = this.props;

    const currentLocaleData = moment.localeData();
    const format = currentLocaleData._longDateFormat.LT.replace(':mm', ''); // do not display minutes
    const result = [];

    for (let i = 0; i < 24; i++) {
      if (limitHours.indexOf(i) > -1) {
        const d = moment
          .utc()
          .hours(i)
          .minutes(0);

        result.push(
          <div class="time-slot" key={i}>
            <span>{d.utc().format(format)}</span>
          </div>
        );
      }
    }

    return result;
  };

  header = () => {
    const { layout, date, highlightSelectedDate, highlightTodaysDate } = this.props;

    let newDate = date.clone().startOf('week');

    const header = [];
    for (let i = 0; i < 7; i++) {
      let highlight = false;
      if ((layout.breakpointIndex < 2 || highlightSelectedDate) && moment(newDate).date() === moment(date).date()) {
        highlight = true;
      } else if (
        layout.breakpointIndex > 1 &&
        highlightTodaysDate &&
        moment(newDate).format('YYYY-MM-DD') === moment().format('YYYY-MM-DD')
      ) {
        highlight = true;
      }

      header.push(
        <div class={'tcell ' + (highlight ? 'active ' : '')} key={i}>
          <div class="weekdate">{newDate.date()}</div>
          <div class="weekday">{newDate.format('dddd')}</div>
        </div>
      );

      newDate = newDate.add(1, 'day');
    }

    return header;
  };

  // render each hour of the day
  apptsList = () => {
    const { limitHours } = this.props;

    const rows = [];
    for (let i = 0; i < 24; i++) {
      if (limitHours.indexOf(i) > -1) {
        rows.push(
          <div class="trow" key={i}>
            {this.days(i)}
          </div>
        );
      }
    }

    return rows;
  };

  // render each hour for each day individually
  days = hour => {
    const { layout, date, highlightSelectedDate, highlightTodaysDate } = this.props;

    let newDate = date.clone().startOf('week');

    const days = [];
    for (let i = 0; i < 7; i++) {
      let highlight = false;
      if ((layout.breakpointIndex < 2 || highlightSelectedDate) && moment(newDate).date() === moment(date).date()) {
        highlight = true;
      } else if (
        layout.breakpointIndex > 1 &&
        highlightTodaysDate &&
        moment(newDate).format('YYYY-MM-DD') === moment().format('YYYY-MM-DD')
      ) {
        highlight = true;
      }

      days.push(
        <div class={'tcell ' + (highlight ? 'active ' : '')} key={i}>
          <div class="cell-inner">{this.appts(newDate, hour, 0)}</div>
          <div class="cell-inner">{this.appts(newDate, hour, 30)}</div>
        </div>
      );

      newDate = moment(newDate).add(1, 'days');
    }

    return days;
  };

  // render appointments for given day and given hour
  appts = (date, hour, minutes) => {
    const { defaultTimezone, getAppts, eventRenderer } = this.props;

    const appts = getAppts(date);
    const result = [];

    for (let i = 0; i < appts.length; i++) {
      const style = {};

      const takesFullDay = appts[i].takes_full_day;
      let startTime = [0, 0];
      let endTime = [23, 45];
      if (!takesFullDay) {
        const apptStartDate = moment.tz(appts[i].date_from, 'UTC').tz(defaultTimezone);
        const apptEndDate = moment.tz(appts[i].date_to, 'UTC').tz(defaultTimezone);
        startTime = [apptStartDate.hours(), apptStartDate.minutes()];
        endTime = [apptEndDate.hours(), apptEndDate.minutes()];
      }

      // get the intersections (how many appointments are there before, and after, this one)
      // this will help us calculate the width and position of the appt on the calendar
      const { countBefore, countAfter } = this.countIntersections(date, appts[i]);

      style.height = (endTime[0] - startTime[0] + (endTime[1] - startTime[1]) / 60) * 80 + 'px'; // each hour is 80 pixel
      style.width = 100 / (countBefore + countAfter + 1) + '%';
      style.left = (100 / (countBefore + countAfter + 1)) * countBefore + '%';

      // check if the appointment is in correct time
      if (
        startTime[0] === hour &&
        startTime[1] >= minutes &&
        ((minutes === 0 && startTime[1] < 30) || (minutes === 30 && startTime[1] < 60))
      ) {
        result.push(React.cloneElement(eventRenderer, { style, appt: appts[i], timezone: defaultTimezone, key: i }));
      }
    }

    return result;
  };

  // how many intersections does the appointment have?
  // needed to calculate the width
  countIntersections = (date, appt) => {
    const { defaultTimezone, getAppts } = this.props;

    const appts = getAppts(date);
    let apptAppeared = false;
    let countBefore = 0;
    let countAfter = 0;

    // get appointment data
    const apptTakesFullDay = appt.takes_full_day;
    let apptStartTime = 0;
    let apptEndTime = 23.75;
    if (!apptTakesFullDay) {
      const apptStartDate = moment.tz(appt.date_from, 'UTC').tz(defaultTimezone);
      apptStartTime = apptStartDate.hours() + apptStartDate.minutes() / 60;

      const apptEndDate = moment.tz(appt.date_to, 'UTC').tz(defaultTimezone);
      apptEndTime = apptEndDate.hours() + apptEndDate.minutes() / 60;
    }

    for (let i = 0; i < appts.length; i++) {
      if (appts[i].id !== appt.id) {
        // get data from looped appointment
        const takesFullDay = appts[i].takes_full_day;
        let startTime = 0;
        let endTime = 23.75;
        if (!takesFullDay) {
          const startDate = moment.tz(appts[i].date_from, 'UTC').tz(defaultTimezone);
          startTime = startDate.hours() + startDate.minutes() / 60;

          const endDate = moment.tz(appts[i].date_to, 'UTC').tz(defaultTimezone);
          endTime = endDate.hours() + endDate.minutes() / 60;
        }

        if (apptTakesFullDay && takesFullDay) {
          if (apptAppeared) {
            countAfter++;
          } else {
            countBefore++;
          }
        } else if (apptStartTime >= startTime && apptStartTime < endTime) {
          countBefore++;
        } else if (startTime >= apptStartTime && startTime <= apptEndTime) {
          countAfter++;
        }
      } else {
        apptAppeared = true;
      }
    }

    return { countBefore, countAfter };
  };

  render() {
    const { renderHours } = this.props;

    return (
      <div id="calendar" class="calendar-container" style={{ height: 'calc(100% - 218px)' }}>
        <div class="view week-view">
          <div class="grid slot-30">
            <Clearfix>
              <div class="allday-cell" />
              <div class="tble" id="viewTableHead">
                <div class="thead">{this.header()}</div>
              </div>
            </Clearfix>
            {renderHours && (
              <div class="time-slot-wrapper" id="time-slots">
                {this.hoursList()}
              </div>
            )}
            <div class="tble" id="weekGrid">
              {this.apptsList()}
            </div>
          </div>
        </div>
      </div>
    );
  }
}
CalendarWeekView.defaultProps = {
  renderHours: true,
  limitHours: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23],
  getAppts: () => {
    return [];
  },
  highlightSelectedDate: true,
  highlightTodaysDate: false,
  eventRenderer: <CalendarWeekEvent />,
};

const mapStateToProps = state => {
  return {
    data: state.calendar,
    defaultTimezone: getTimezoneFromState(state),
    layout: state.layout,
  };
};
const mapDispatchToProps = (dispatch, ownProps) => {
  const res = {};

  if (!ownProps.doFetchCalendar) {
    res.doFetchCalendar = (startDate, endDate) => {
      dispatch(fetchCalendar(startDate, endDate));
    };
  }

  return res;
};

export default withRouter(
  injectIntl(
    connect(
      mapStateToProps,
      mapDispatchToProps
    )(CalendarWeekView)
  )
);
